import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { parseReportSearch, type ReportSearch } from '../../features/reports/range';
import { InsurancePaymentsReportScreen } from '../../features/reports/report-screens';
import { REPORT_PERMISSIONS } from '../../features/reports/types';
import { RequirePermission } from '../../lib/session/guards';
import { usePermissions } from '../../lib/session/permissions';

export const Route = createFileRoute('/reports/insurance-payments')({
  validateSearch: (search: Record<string, unknown>): ReportSearch => parseReportSearch(search),
  component: InsurancePaymentsReportPage,
});

/**
 * `/reports/insurance-payments` — eligibility outcomes and money (FR-REPORT-02).
 *
 * `reports.finance` opens it. The uncharged-visit rows link into `/appointments/$id`,
 * which is behind `appointments.read`; a role holding the finance key but not that one
 * gets the list and a link that refuses, which is the server's decision to make and not
 * this screen's (rules §17.2, §22.2).
 */
function InsurancePaymentsReportPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);
  const { can } = usePermissions();

  return (
    <RequirePermission keys={[REPORT_PERMISSIONS['insurance-payments']]}>
      <InsurancePaymentsReportScreen
        search={search}
        onSearchChange={(next) => {
          void navigate({ to: '/reports/insurance-payments', search: next, replace: true });
        }}
        now={now}
        can={can}
        backLink={
          <Link to="/reports" search={search} className="text-primary-accessible hover:underline">
            Reports
          </Link>
        }
        renderAppointmentLink={(appointmentId, children) => (
          <Link
            to="/appointments/$appointmentId"
            params={{ appointmentId }}
            className="font-medium text-primary-accessible hover:underline"
          >
            {children}
          </Link>
        )}
      />
    </RequirePermission>
  );
}
